import { Typography } from 'antd'
import { observer } from 'mobx-react-lite'

import { Hint } from '@/Ship/Components/Hint'
import { formatDateUk } from '@/Ship/Helpers/fieldViews'

import { CrmChildListTab } from '../ChildList/CrmChildListTab'
import { CrmFileLinks } from '../ChildList/CrmFileLinks'
import { CRM_DOCUMENT_FIELD_LABELS as L } from './CrmDocument'
import { CRM_DOCUMENT_GENERATION } from './GenerateDocument'
import { GenerateDocumentMenu } from './GenerateDocumentMenu'

import { crmOwnerOf } from '../Owner/CrmOwnerFormContext'

import type { CrmOwnerFormContextLike } from '../Owner/CrmOwnerFormContext'
import type { CrmDocumentRow } from './CrmDocument'
import type { TableColumnsType } from 'antd'

const { Text } = Typography

const dash = <Text type="secondary">—</Text>

const textOrDash = (value: unknown) => (typeof value === 'string' && value !== '' ? value : dash)

/**
 * Колонки — рекомендація `SPEC-012` §9.2 (`SPEC-009` §8.4): назва, тип,
 * номер, дата, Word і PDF. Файли — підписаними посиланнями в новій вкладці.
 */
const COLUMNS: TableColumnsType<CrmDocumentRow> = [
    { title: L.title, key: 'title', dataIndex: 'title', render: textOrDash },
    {
        title: L.documentType,
        key: 'documentType',
        dataIndex: 'documentType',
        render: (value: CrmDocumentRow['documentType']) => value?.title || dash,
    },
    { title: L.number, key: 'number', dataIndex: 'number', render: textOrDash },
    {
        title: L.date,
        key: 'date',
        dataIndex: 'date',
        render: (value: unknown) => (value ? formatDateUk(value) : dash),
    },
    {
        title: L.wordFile,
        key: 'wordFile',
        dataIndex: 'wordFile',
        render: (value: unknown) => <CrmFileLinks value={value} />,
    },
    {
        title: L.pdfFile,
        key: 'pdfFile',
        dataIndex: 'pdfFile',
        render: (value: unknown) => <CrmFileLinks value={value} />,
    },
]

export interface CrmDocumentsTabProps {
    /** Ключ ресурсу документа: `Crm.ClientDocument` */
    resource: string
    /** Колекція документів: `/services/client_documents` */
    restUrl: string
    /** Поле власника в документі: `client`, `carrier` */
    ownerField: string
    /** `formContext` картки власника — звідти `uuid`, `@id` і `_permissions.update` */
    ownerFormContext: CrmOwnerFormContextLike | undefined
}

/**
 * Підказка над списком документів (`SPEC-012` §9.2, §13, `crm.documents.pdf`).
 *
 * Твердження звірено з кодом: PDF робить LibreOffice з Word
 * (`ReplaceDocumentWordTask`) — після генерації, після ручного завантаження
 * Word (`AbstractUploadCrmDocumentWordFileAction`) і після кожного збереження в
 * Collabora (WOPI PutFile → `SaveEditedDocumentWordTask`, S7b), а ручний PDF
 * при прикріпленому Word бек відхиляє (`AbstractUploadCrmDocumentPdfFileAction`, 422).
 */
const CrmDocumentsPdfHint = () => (
    <Hint id="crm.documents.pdf">
        PDF формується з Word автоматично — після генерації, завантаження Word і кожного збереження в редакторі.
        Поки до документа прикріплено Word, PDF вручну не завантажити.
    </Hint>
)

/**
 * Вкладка «Друковані форми» картки власника CRM (`SPEC-008` §7.1,
 * `SPEC-009` §8.4): документи власника з файлами Word і PDF.
 *
 * «Додати документ» — ручне створення, за `_permissions.update` власника
 * (`SPEC-009` §9.2), а стрілка поруч — меню «Генерація документу» з типами
 * свого провайдера (`SPEC-012` §9.2–§9.3, `GenerateDocumentMenu`). Право те
 * саме: генерує той, хто змінює власника (P-10, Q12.10). У заявки номер
 * документа за замовчуванням — її Ід (`CRM_DOCUMENT_GENERATION.application`).
 *
 * Коли вкладку показувати (лише верифікованому, КЛ-26, R-13), вирішує картка
 * власника; бек генерації статус перевіряє й сам (P-11).
 */
export const CrmDocumentsTab = observer(({ resource, restUrl, ownerField, ownerFormContext }: CrmDocumentsTabProps) => {
    const generation = CRM_DOCUMENT_GENERATION[ownerField]
    const ownerUuid = crmOwnerOf(ownerFormContext)?.uuid
    const defaultNumberValue = generation?.defaultNumberField ? ownerFormContext?.values?.[generation.defaultNumberField] : undefined
    const defaultNumber =
        typeof defaultNumberValue === 'number' || typeof defaultNumberValue === 'string' ? String(defaultNumberValue) : undefined

    return (
        <>
            <CrmDocumentsPdfHint />
            <CrmChildListTab<CrmDocumentRow>
                resource={resource}
                restUrl={restUrl}
                ownerField={ownerField}
                ownerFormContext={ownerFormContext}
                canAdd={ownerFormContext?.values?._permissions?.update === true}
                {...(generation && ownerUuid
                    ? {
                          renderAdd: ({ create, reload }: { create: () => void; reload: () => void }) => (
                              <GenerateDocumentMenu
                                  config={generation}
                                  ownerField={ownerField}
                                  ownerUuid={ownerUuid}
                                  {...(defaultNumber !== undefined ? { defaultNumber } : {})}
                                  onCreate={create}
                                  onGenerated={reload}
                              />
                          ),
                      }
                    : {})}
                columns={COLUMNS}
                texts={{
                    add: 'Додати документ',
                    empty: 'Документів ще немає.',
                    loadFailed: 'Не вдалося завантажити документи.',
                    removeTitle: 'Видалити документ?',
                    removeDescription: 'Документ зникне зі списку друкованих форм.',
                    removeLabel: 'Видалити документ',
                    countNoun: 'документів',
                }}
            />
        </>
    )
})
